/**
 * A tela de Empresas (história 2.6, FR-30, NFR-1, NFR-3).
 *
 * A porta de entrada da base comercial: buscar, paginar e abrir a ficha. Até aqui `/empresas`
 * era placeholder e quem criava uma ficha só voltava a ela digitando a URL.
 *
 * **Este é o primeiro componente do repositório que troca de árvore por viewport, e a saída do
 * padrão é deliberada.** Toda tela até aqui reflui numa árvore só, sem `@media`, e o CSS dos
 * módulos diz isso em voz alta. Esta não pode: o protótipo desenha **duas coisas diferentes** —
 * uma tabela de cinco colunas e um card empilhado —, com inclusive duas receitas de cor para a
 * mesma tag de estado (fundo tingido no computador, texto colorido em chip neutro no celular).
 * Reflui-las uma na outra por CSS obrigaria a apagar o `display` nativo da `<table>`, e com ele
 * os papéis implícitos `table`/`row`/`cell` que o leitor de tela usa para dizer "coluna Setor,
 * Agronegócio" — a tabela continuaria no HTML mentindo que é uma tabela.
 *
 * A virada é por `useEhCelular`, o mesmo mecanismo — e pelo mesmo motivo — que o shell já usa
 * para escolher entre sidebar e barra inferior: duas árvores coexistindo escondidas por CSS
 * dariam dois "Orvalho do Sol Ltda" na mesma página, um invisível, e todo teste teria de
 * desempatar entre eles. O preço é que a paridade da NFR-1 deixa de poder ser afirmada por
 * igualdade de DOM: o que a suíte afirma é que a mesma **informação** e as mesmas **ações**
 * existem nos dois lados.
 *
 * **A busca e a página são da URL, e chegam por props.** Quem as valida e as escreve é
 * `rotas/empresas.tsx`; esta tela não guarda nenhuma das duas em estado local. O motivo está lá:
 * elas são o "onde eu estou" do operador, e em `useState` se perdem ao recarregar, ao voltar de
 * uma ficha e ao atravessar os 768px do shell — que remonta o `<Outlet/>`.
 *
 * **Os dois nomes que a lista não traz vêm daqui, e isso não é atalho.** O serviço devolve
 * `responsavel_id` e `setor_id`, e não os nomes: `usuario` é de outro serviço e de outro
 * database (AD-21/AD-22), e o rótulo do Setor o SPA já tem em cache. `useResponsaveis()` e
 * `useOpcoesDeDominio()` são duas consultas para a tela inteira — e não duas por linha.
 * `useRotulosDeOpcoes` entra só para os `setor_id` que o vocabulário ativo não resolve, que são
 * os valores excluídos das listas (FR-67) e tendem a zero.
 *
 * **A coluna "Sem contato" existe e está vazia.** O dono do último contato é o serviço
 * `negocios`, e a régua de dias é do Épico 3. Usar `criado_em` como proxy diria ao Vendedor que
 * uma ficha de ontem está "1 dia sem contato" quando ninguém falou com ela nunca. O slot existe,
 * mostra `—`, e a linha de ajuda diz de onde o número virá — a mesma regra das badges e dos
 * subtítulos do shell.
 *
 * **As colunas são configuráveis, e só o nome é link** (11.7, AD-29). A tabela é TanStack Table
 * (`columnVisibilityFeature`): a pessoa mostra ou esconde Etiquetas, Setor, Responsável, Faixa, Cidade,
 * UF e Sem contato pelo menu "Colunas" — a Razão social não se esconde, porque é o nome da linha e
 * o único link dela. A preferência é **do navegador** (`localStorage`, `CHAVE_DAS_COLUNAS`), e não
 * dado de servidor: não é sessão nem credencial, e não atravessa dispositivos por desenho. **Não
 * existe coluna "Estado"/UF digitada**: a UF é lida do endereço principal da ficha. O padrão é o
 * das cinco colunas do protótipo; Faixa, Cidade e UF entram por escolha. No celular, os cards
 * trazem no resumo as mesmas colunas escolhidas (paridade NFR-1).
 *
 * **Sem `<h1>`, e sem repetir o título do shell.** O cabeçalho do shell já traz "Empresas"; o
 * cartão daqui se chama "Base comercial", como as "Listas de domínio" da 2.3 sob o título
 * "Administração" — dois marcos com o mesmo nome na mesma página são dois marcos que quem navega
 * por leitor de tela não consegue distinguir. O que esta tela empresta ao cabeçalho é o
 * **subtítulo**, por `useDefinirSubtitulo`: as contagens só existem depois da resposta, e
 * dependem de quem está lendo.
 *
 * **Desde a 13.6 (chamado de 21/10, P5) a tela não mostra estado da Empresa** — nem coluna, nem
 * tag, nem o subtítulo com as duas contagens, nem a cor da borda do card: com as etiquetas da equipe,
 * ele confundiria (decisão do Gustavo). Desde a 13.11b o estado nem existe mais: quem enxerga a ficha
 * é a regra "é responsável, vê", e a ida ao Carga Máquina é o primeiro orçamento com CNPJ. No lugar
 * da coluna Estado, a coluna **Etiquetas** (as da
 * equipe, D-8); "Sem responsável" passou à coluna Responsável, em vermelho; a barra ganhou o filtro
 * por etiqueta e o botão "Etiquetas" (o gerenciador, que todo perfil abre); o subtítulo é "N empresas",
 * como o da tela Pessoas. Quem chega pelo "Ver empresas" do aviso de apagar lê quantas fichas com
 * aquela etiqueta não enxerga (D-11).
 */

import {
  type ColumnVisibilityState,
  columnVisibilityFeature,
  createColumnHelper,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import { Link } from '@tanstack/react-router'
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { CAMINHO_DA_EMPRESA, CAMINHO_DA_EMPRESA_NOVA } from '@/rotas/empresa'
import { AcoesDoCabecalho, useDefinirSubtitulo, useEhCelular } from '@/shell/Shell'
import type { EmpresaNaLista } from './api'
import {
  TAMANHO_DA_BUSCA,
  etiquetaIdsDe,
  useEmpresas,
  useOpcoesDeDominio,
  useRotulosDeOpcoes,
} from './api'
import {
  BotaoDeEtiquetas,
  ChipsDeEtiquetas,
  FiltroPorEtiqueta,
  GerenciadorDeEtiquetas,
  FILTRO_SEM_A_ETIQUETA,
  useAvisoDasEscondidas,
  useEtiquetaDoFiltro,
} from './etiquetas'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import { ROTULOS_DA_FAIXA } from './faixa'
import './lista-de-empresas.css'

/**
 * Quantas fichas a tela pede por página.
 *
 * Ela **pede** o limite em vez de aceitar o padrão do serviço porque é ela quem calcula a
 * posição ("51–100 de 178") e quantas páginas existem: com o número implícito, um ajuste do
 * padrão do lado de lá faria a posição mentir sem nada falhar.
 */
export const LIMITE_DA_PAGINA = 50

/**
 * As colunas da tabela, na ordem da tela, com o cabeçalho literal do protótipo (`:1224`) para as
 * cinco dele e os três da 11.7. `nome` é a única que não se esconde.
 */
const ROTULOS_DAS_COLUNAS = {
  nome: 'Empresa',
  cnpj: 'CNPJ',
  etiquetas: 'Etiquetas',
  setor: 'Setor',
  responsavel: 'Responsável',
  faixa: 'Faixa',
  cidade: 'Cidade',
  uf: 'UF',
  sem_contato: 'Sem contato',
} as const

type IdDaColuna = keyof typeof ROTULOS_DAS_COLUNAS

/** As que o menu "Colunas" oferece — todas menos a Razão social. */
const COLUNAS_CONFIGURAVEIS = (Object.keys(ROTULOS_DAS_COLUNAS) as IdDaColuna[]).filter(
  (id) => id !== 'nome',
)

/** O padrão: as cinco do protótipo; Faixa, Cidade e UF entram por escolha. */
export const COLUNAS_VISIVEIS_POR_PADRAO: Record<IdDaColuna, boolean> = {
  nome: true,
  cnpj: true,
  etiquetas: true,
  setor: true,
  responsavel: true,
  faixa: false,
  cidade: false,
  uf: false,
  sem_contato: true,
}

/**
 * Onde a preferência de colunas mora — no **navegador** (a spec: "preferência por usuário no
 * navegador — não é dado de servidor"). Não é sessão nem credencial: a proibição do repositório
 * sobre `localStorage` é do token de sessão.
 */
export const CHAVE_DAS_COLUNAS = 'crm.empresas.colunas-visiveis'

/** A preferência gravada, só com as colunas que existem e só com booleanos — o resto cai. */
function lerAsColunas(): Record<IdDaColuna, boolean> {
  try {
    const gravado: unknown = JSON.parse(localStorage.getItem(CHAVE_DAS_COLUNAS) ?? '{}')
    if (typeof gravado !== 'object' || gravado === null) {
      return COLUNAS_VISIVEIS_POR_PADRAO
    }
    const escolhidas = { ...COLUNAS_VISIVEIS_POR_PADRAO }
    for (const id of COLUNAS_CONFIGURAVEIS) {
      const valor = (gravado as Record<string, unknown>)[id]
      if (typeof valor === 'boolean') {
        escolhidas[id] = valor
      }
    }
    return escolhidas
  } catch {
    // Navegador sem armazenamento (janela privada, bloqueio): vale o padrão, e nada quebra.
    return COLUNAS_VISIVEIS_POR_PADRAO
  }
}

function gravarAsColunas(colunas: ColumnVisibilityState): void {
  try {
    localStorage.setItem(CHAVE_DAS_COLUNAS, JSON.stringify(colunas))
  } catch {
    // Sem armazenamento, a escolha vale até recarregar — e só.
  }
}

const ID_DO_TITULO = 'empresas-titulo'
const ID_DA_BUSCA = 'empresas-busca'
const ID_DO_GERENCIADOR = 'empresas-etiquetas'

/**
 * O nome acessível do cartão — **diferente** do título da tela no shell, de propósito.
 *
 * O `<h1>` do shell já diz "Empresas". Um `<h2>Empresas</h2>` aqui daria dois marcos homônimos
 * e obrigaria quem navega por leitor de tela a entrar em cada um para descobrir a diferença.
 */
const NOME_DO_CARTAO = 'Base comercial'

/** O nome do embrulho rolável da tabela — ele é foco de teclado, e foco precisa de nome. */
const NOME_DA_AREA_ROLAVEL = 'Tabela de empresas'

/**
 * O nome das duas listas — a `<caption>` da tabela e o `aria-label` dos cards —, e ele é **um**.
 *
 * Um só pelos dois motivos desta tela: `Empresas` seco repetiria o `<h1>` do shell, do mesmo
 * jeito que o título do cartão evita (ver `NOME_DO_CARTAO`); e a ordem alfabética era
 * informação que o computador tinha na legenda e o celular não tinha em lugar nenhum —
 * paridade da NFR-1 é a mesma informação nas duas árvores, e isto era uma frase de diferença.
 */
const NOME_DA_LISTA = 'Empresas, em ordem alfabética'

const ROTULO_DA_BUSCA = 'Buscar empresa'

/**
 * A ajuda do cabeçalho: a frase do protótipo **e** a explicação da coluna vazia.
 *
 * A segunda metade não é enfeite — uma coluna inteira de traços obriga quem abriu a perguntar,
 * e a resposta honesta é que o dado ainda não existe neste sistema.
 */
const AJUDA =
  'A coluna Sem contato fica em — até o dado de último contato chegar com os Negócios, no ' +
  'Épico 3: nenhum número é inventado.'

const CARREGANDO = 'Carregando as empresas…'
const ATUALIZANDO = 'Atualizando…'
const SEM_EMPRESAS = 'Nenhuma empresa cadastrada ainda.'
const SEM_RESULTADO = 'Nenhuma empresa encontrada para esta busca.'
const PAGINA_VAZIA = 'Esta página não existe mais — voltando para a última que existe.'
const TENTAR_DE_NOVO = 'Tentar de novo'

/** O que uma célula mostra quando o valor não existe ou não pôde ser resolvido. */
const VAZIO = '—'

/** Formatador de contagem em português do Brasil (NFR-8): milhar com ponto. */
const NUMERO = new Intl.NumberFormat('pt-BR')

/** O que a coluna Responsável diz da ficha que ninguém atende (FR-34), em vermelho (13.6). */
export const SEM_RESPONSAVEL = 'Sem responsável'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** `"21.460 empresas"` — o subtítulo do cabeçalho (13.6, o molde da tela Pessoas). */
function subtituloDaBase(total: number): string {
  return `${NUMERO.format(total)} ${total === 1 ? 'empresa' : 'empresas'}`
}

/** O que uma ficha mostra, já com os dois nomes resolvidos pelos caches da tela. */
interface LinhaDaTela {
  empresa: EmpresaNaLista
  /** A ficha órfã (FR-34): "Sem responsável" em vermelho na coluna Responsável. */
  semResponsavel: boolean
  setor: string
  responsavel: string
  faixa: string
  cidade: string
  uf: string
}

/** As capacidades da tabela, registradas uma vez no módulo (v9: só o que se usa). */
const recursos = tableFeatures({ columnVisibilityFeature })

const coluna = createColumnHelper<typeof recursos, LinhaDaTela>()

/** As colunas, na ordem da tela. As células são desenhadas em `Tabela` pelo `id`. */
const COLUNAS = coluna.columns([
  coluna.display({ id: 'nome', header: ROTULOS_DAS_COLUNAS.nome, enableHiding: false }),
  coluna.display({ id: 'cnpj', header: ROTULOS_DAS_COLUNAS.cnpj }),
  coluna.display({ id: 'etiquetas', header: ROTULOS_DAS_COLUNAS.etiquetas }),
  coluna.accessor('setor', { id: 'setor', header: ROTULOS_DAS_COLUNAS.setor }),
  coluna.accessor('responsavel', { id: 'responsavel', header: ROTULOS_DAS_COLUNAS.responsavel }),
  coluna.accessor('faixa', { id: 'faixa', header: ROTULOS_DAS_COLUNAS.faixa }),
  coluna.accessor('cidade', { id: 'cidade', header: ROTULOS_DAS_COLUNAS.cidade }),
  coluna.accessor('uf', { id: 'uf', header: ROTULOS_DAS_COLUNAS.uf }),
  coluna.display({ id: 'sem_contato', header: ROTULOS_DAS_COLUNAS.sem_contato }),
])

/** A classe do `<col>` de cada coluna — a largura mora no CSS (`table-layout: fixed`). */
const CLASSE_DA_COLUNA: Record<IdDaColuna, string> = {
  nome: 'empresas__col--nome',
  cnpj: 'empresas__col--cnpj',
  etiquetas: 'empresas__col--etiquetas',
  setor: 'empresas__col--texto',
  responsavel: 'empresas__col--texto',
  faixa: 'empresas__col--curta',
  cidade: 'empresas__col--texto',
  uf: 'empresas__col--dias',
  sem_contato: 'empresas__col--dias',
}

/** O menu "Colunas": mostrar e esconder — a escolha fica no navegador. */
function MenuDeColunas({
  visiveis,
  aoTrocar,
}: {
  visiveis: Record<IdDaColuna, boolean>
  aoTrocar: (id: IdDaColuna, visivel: boolean) => void
}) {
  return (
    <details className="empresas__colunas">
      <summary className="empresas__botao empresas__botao--discreto">Colunas</summary>
      <fieldset className="empresas__menu-de-colunas">
        <legend className="empresas__rotulo">Colunas visíveis</legend>
        {COLUNAS_CONFIGURAVEIS.map((id) => (
          <label key={id} className="empresas__opcao-de-coluna">
            <input
              type="checkbox"
              checked={visiveis[id]}
              onChange={(evento) => {
                aoTrocar(id, evento.target.checked)
              }}
            />
            {ROTULOS_DAS_COLUNAS[id]}
          </label>
        ))}
      </fieldset>
    </details>
  )
}

function Tabela({
  linhas,
  visiveis,
}: {
  linhas: LinhaDaTela[]
  visiveis: Record<IdDaColuna, boolean>
}) {
  const tabela = useTable({
    features: recursos,
    columns: COLUNAS,
    data: linhas,
    state: { columnVisibility: visiveis },
    getRowId: (linha) => linha.empresa.id,
  })
  const colunasVisiveis = tabela.getVisibleLeafColumns()

  return (
    /*
     * O embrulho rolável é do **`<div>`**, e nunca da `<table>`: mexer no `display` da tabela
     * (ou dar-lhe `overflow`) descarta os papéis implícitos `table`/`rowgroup`/`row`/`cell` no
     * navegador de verdade — o jsdom não reproduz isso, então a suíte passaria com a semântica
     * destruída. `tabIndex={0}` + nome acessível porque uma área que rola precisa ser alcançável
     * por teclado: sem eles, quem não usa ponteiro não chega à coluna "Sem contato" (WCAG 2.1.1).
     */
    <div className="empresas__rolagem" role="region" aria-label={NOME_DA_AREA_ROLAVEL} tabIndex={0}>
      <table className="empresas__tabela">
        {/* A largura das colunas mora no `<colgroup>`, e não num `display:grid` que apagaria a
            tabela: com `table-layout: fixed` estas são as medidas finais, e elas reproduzem a
            proporção do protótipo (`minmax(0,1.5fr) 92px 1fr 1fr 76px`). */}
        <colgroup>
          {colunasVisiveis.map((visivel) => (
            <col key={visivel.id} className={CLASSE_DA_COLUNA[visivel.id as IdDaColuna]} />
          ))}
        </colgroup>
        <caption className="empresas__legenda">{NOME_DA_LISTA}</caption>
        <thead>
          {tabela.getHeaderGroups().map((grupo) => (
            <tr key={grupo.id}>
              {grupo.headers.map((cabecalho) => (
                <th key={cabecalho.id} scope="col" className="empresas__coluna">
                  <tabela.FlexRender header={cabecalho} />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {tabela.getRowModel().rows.map((linhaDaTabela) => {
            const { empresa, semResponsavel } = linhaDaTabela.original
            return (
              <tr key={empresa.id} className="empresas__linha">
                {/* `<th scope="row">`: a razão social é o nome da linha, e é por ela que o leitor
                  de tela anuncia cada célula seguinte. O CNPJ vai **dentro** desta célula, como
                  segunda linha — no protótipo ele não é coluna.

                  As duas linhas empilham num `<div>` **dentro** da célula, e não na célula: dar
                  `display: flex` a um `<th>` faz o navegador descartar-lhe o papel `rowheader`,
                  que é a mesma armadilha que o cabeçalho de `lista-de-empresas.css` descreve
                  para `<table>`/`<tr>` — e que o jsdom, que deriva papel do nome da tag, não
                  reprovaria. */}
                {linhaDaTabela.getVisibleCells().map((celula) => {
                  const id = celula.column.id as IdDaColuna
                  if (id === 'nome') {
                    return (
                      <th key={celula.id} scope="row" className="empresas__celula">
                        <div className="empresas__nome">
                          {/* O **único** link da linha (Interaction Primitives: só o nome abre). */}
                          <Link
                            to={CAMINHO_DA_EMPRESA}
                            params={{ empresaId: empresa.id }}
                            className="empresas__link"
                          >
                            {nomeExibidoDaEmpresa(empresa)}
                          </Link>
                          {/* 13.14a (DW-415): com a coluna Responsável escondida, a ficha órfã
                              continua dizendo que ninguém a atende — o selo junto do nome. */}
                          {semResponsavel && !visiveis.responsavel ? (
                            <span
                              className="empresas__selo-sem-responsavel"
                              data-sem-responsavel="sim"
                            >
                              {SEM_RESPONSAVEL}
                            </span>
                          ) : null}
                        </div>
                      </th>
                    )
                  }
                  {/* O CNPJ é coluna própria, em mono (protótipo v2). */}
                  if (id === 'cnpj') {
                    return (
                      <td key={celula.id} className="empresas__celula empresas__celula--cnpj empresas__documento numero">
                        {empresa.documento ?? VAZIO}
                      </td>
                    )
                  }
                  if (id === 'etiquetas') {
                    return (
                      <td key={celula.id} className="empresas__celula">
                        <ChipsDeEtiquetas tipo="empresa" ids={etiquetaIdsDe(empresa)} maximo={3} />
                      </td>
                    )
                  }
                  if (id === 'responsavel' && semResponsavel) {
                    return (
                      <td
                        key={celula.id}
                        className="empresas__celula empresas__celula--sem-responsavel"
                        data-sem-responsavel="sim"
                      >
                        {SEM_RESPONSAVEL}
                      </td>
                    )
                  }
                  if (id === 'sem_contato') {
                    /* O slot dos dias, vazio de propósito: o dado é do Épico 3. */
                    return (
                      <td key={celula.id} className="empresas__celula empresas__celula--dias">
                        {VAZIO}
                      </td>
                    )
                  }
                  return (
                    <td
                      key={celula.id}
                      className={
                        id === 'uf'
                          ? 'empresas__celula empresas__celula--apagada numero'
                          : 'empresas__celula empresas__celula--apagada'
                      }
                    >
                      <tabela.FlexRender cell={celula} />
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** A linha-resumo do card: as colunas escolhidas, na ordem da tabela (paridade NFR-1). */
function resumoDoCard(linha: LinhaDaTela, visiveis: Record<IdDaColuna, boolean>): string {
  const partes: string[] = []
  if (visiveis.setor) partes.push(linha.setor)
  // "Sem responsável" sai fora do resumo, num `span` próprio e vermelho (13.6).
  if (visiveis.responsavel && !linha.semResponsavel) partes.push(linha.responsavel)
  if (visiveis.faixa) partes.push(linha.faixa)
  /* Cidade e UF entram só com valor: "—/—" (ou uma parte vazia) seria a tela falando de um
     endereço que a ficha não tem. As duas ausentes, a parte some. */
  const lugar = [visiveis.cidade ? linha.cidade : VAZIO, visiveis.uf ? linha.uf : VAZIO]
    .filter((parte) => parte !== VAZIO)
    .join('/')
  if (lugar !== '') {
    partes.push(lugar)
  }
  if (visiveis.sem_contato) partes.push(`${VAZIO} sem contato`)
  return partes.join(' · ')
}

function Cards({
  linhas,
  visiveis,
}: {
  linhas: readonly LinhaDaTela[]
  visiveis: Record<IdDaColuna, boolean>
}) {
  return (
    <ul className="empresas__cards" aria-label={NOME_DA_LISTA}>
      {linhas.map((linha) => {
        const { empresa, semResponsavel } = linha
        const resumo = resumoDoCard(linha, visiveis)
        return (
          <li
            key={empresa.id}
            className="empresas__card"
            data-sem-responsavel={semResponsavel ? 'sim' : undefined}
          >
            <div className="empresas__card-topo">
              <Link
                to={CAMINHO_DA_EMPRESA}
                params={{ empresaId: empresa.id }}
                className="empresas__link empresas__card-titulo"
              >
                {nomeExibidoDaEmpresa(empresa)}
              </Link>
            </div>
            <span className="empresas__documento numero">{empresa.documento ?? VAZIO}</span>
            {/* A linha-resumo do protótipo (`:1027`), com o slot dos dias no lugar do número que
              ainda não existe. A mesma informação da tabela — as colunas escolhidas. */}
            {resumo === '' ? null : <span className="empresas__card-resumo">{resumo}</span>}
            {visiveis.responsavel && semResponsavel ? (
              <span className="empresas__card-sem-responsavel" data-sem-responsavel="sim">
                {SEM_RESPONSAVEL}
              </span>
            ) : null}
            {visiveis.etiquetas ? (
              <ChipsDeEtiquetas tipo="empresa" ids={etiquetaIdsDe(empresa)} maximo={3} />
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

export interface PropsDaListaDeEmpresas {
  /** O termo, como a URL o carrega. String vazia é "ninguém buscou nada". */
  busca: string
  /** A página aberta, começando em 1. */
  pagina: number
  /** A etiqueta da equipe do filtro (13.6), como a URL a carrega. */
  etiqueta?: string
  /** Escreve o termo novo na URL. A tela nunca guarda o termo por conta própria. */
  aoTrocarABusca: (termo: string) => void
  /** Escreve (ou tira) o filtro por etiqueta na URL. */
  aoTrocarAEtiqueta?: (etiquetaId: string | undefined) => void
  /**
   * Escreve a página nova na URL.
   *
   * `substituir` troca a entrada de histórico em vez de empurrar outra. Ele é para a **correção**
   * de uma página que não existe mais: empurrada, o botão "voltar" devolveria a pessoa à página
   * inválida, que se corrigiria de novo para frente — um laço do qual só se sai desistindo de
   * voltar. Paginar de propósito continua empurrando, que é o que todo mundo espera do "voltar".
   */
  aoTrocarAPagina: (pagina: number, opcoes?: { substituir?: boolean }) => void
}

export function ListaDeEmpresas({
  busca,
  pagina,
  etiqueta,
  aoTrocarABusca,
  aoTrocarAEtiqueta,
  aoTrocarAPagina,
}: PropsDaListaDeEmpresas) {
  const ehCelular = useEhCelular()
  const [gerenciando, setGerenciando] = useState(false)
  const botaoDeEtiquetas = useRef<HTMLButtonElement>(null)
  const [visiveis, setVisiveis] = useState<Record<IdDaColuna, boolean>>(lerAsColunas)
  const trocarAColuna = (id: IdDaColuna, visivel: boolean) => {
    setVisiveis((atuais) => {
      const novas = { ...atuais, [id]: visivel }
      gravarAsColunas(novas)
      return novas
    })
  }

  /*
   * O campo responde a cada tecla; a **consulta** anda atrás, na renderização de baixa prioridade.
   *
   * O caminho da busca é uma varredura `ILIKE '%…%'`, que índice nenhum acelera — então digitar
   * depressa não deve arrastar a tela junto. `useDeferredValue` adia a **renderização** com o valor novo e mantém o antigo enquanto
   * ela não fica pronta, sem um `setTimeout` para limpar. O que ele **não** é: uma garantia de
   * quantas idas à rede acontecem. Ele não tem relógio, e numa máquina rápida cada valor que
   * assenta ainda é uma consulta; o que ele evita é a tela travar entre uma tecla e a seguinte.
   * Um teto por tempo, se um dia for preciso, é outro mecanismo.
   */
  const termoConsultado = useDeferredValue(busca)

  /* 13.18: a etiqueta da URL que o catálogo de empresa não tem não vai ao serviço — a lista nem sai. */
  const filtro = useEtiquetaDoFiltro('empresa', etiqueta)
  const consulta = useEmpresas(
    {
      busca: termoConsultado,
      pagina,
      limite: LIMITE_DA_PAGINA,
      etiquetaId: etiqueta,
    },
    { habilitada: filtro.pronta },
  )
  const vocabulario = useOpcoesDeDominio()
  const equipe = useResponsaveis()

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data])

  /** `id -> rótulo` das opções de Setor **vivas**, que é o que o vocabulário traz. */
  const setoresVivos = useMemo(
    () => new Map((vocabulario.data?.setor ?? []).map((opcao) => [opcao.id, opcao.rotulo])),
    [vocabulario.data],
  )

  /**
   * Os `setor_id` desta página que o vocabulário **não** resolve.
   *
   * São os valores excluídos das listas: o FR-67 manda continuar exibindo o rótulo nos
   * registros antigos, e a rota por `id` é a única que os devolve. Tendem a zero, e por isso
   * não vale trazer o rótulo pronto em cada linha do serviço.
   *
   * O `isSuccess` é o que impede a rajada: enquanto o vocabulário está no ar, `setoresVivos`
   * está vazio e **todo** `setor_id` da página pareceria excluído — cinquenta linhas virariam
   * cinquenta `GET /opcoes-de-dominio/{id}`, o N+1 que este cálculo existe para evitar.
   */
  const setoresExcluidos = useMemo(() => {
    if (!vocabulario.isSuccess) {
      return []
    }
    const pendentes = new Set<string>()
    for (const empresa of itens) {
      if (empresa.setor_id !== null && !setoresVivos.has(empresa.setor_id)) {
        pendentes.add(empresa.setor_id)
      }
    }
    return [...pendentes]
  }, [itens, setoresVivos, vocabulario.isSuccess])

  const setoresResolvidos = useRotulosDeOpcoes(setoresExcluidos)

  const nomesDaEquipe = useMemo(
    () => new Map((equipe.data ?? []).map((pessoa) => [pessoa.id, pessoa.nome])),
    [equipe.data],
  )

  const linhas: LinhaDaTela[] = itens.map((empresa) => ({
    empresa,
    semResponsavel: empresa.responsavel_id === null,
    setor:
      empresa.setor_id === null
        ? VAZIO
        : (setoresVivos.get(empresa.setor_id) ?? setoresResolvidos.get(empresa.setor_id) ?? VAZIO),
    /* Responsável que o cache não conhece degrada em silêncio: mostrar o uuid cru seria
       mostrar um dado que não diz nada a ninguém, e travar a tela por isso seria pior. */
    responsavel:
      empresa.responsavel_id === null
        ? VAZIO
        : (nomesDaEquipe.get(empresa.responsavel_id) ?? VAZIO),
    faixa: empresa.faixa ? ROTULOS_DA_FAIXA[empresa.faixa] : VAZIO,
    cidade: empresa.cidade ?? VAZIO,
    // A UF **lida do endereço** da ficha (Região = UF), e nunca digitada na lista.
    uf: empresa.uf ?? VAZIO,
  }))

  const total = consulta.data?.total ?? 0

  /* O subtítulo só existe quando há resposta: antes dela, o cabeçalho fica com o do item de
     navegação (que para Empresas é vazio), e não com "0 empresas" — um número que ainda não foi
     medido não pode ser afirmado. `total` é o total do filtro (13.11b: o serviço deixou de
     separar duas contagens). */
  useDefinirSubtitulo(consulta.data === undefined ? undefined : subtituloDaBase(total))

  const avisoDasEscondidas = useAvisoDasEscondidas({
    tipo: 'empresas',
    etiquetaId: etiqueta,
    busca: termoConsultado,
    totalVisivel:
      consulta.data === undefined || consulta.isPlaceholderData || consulta.isFetching
        ? undefined
        : total,
  })

  const totalDePaginas = Math.max(1, Math.ceil(total / LIMITE_DA_PAGINA))
  const primeiroDaPagina = (pagina - 1) * LIMITE_DA_PAGINA + 1
  const posicaoAtual =
    itens.length === 0
      ? `Página ${String(pagina)} de ${String(totalDePaginas)}`
      : `${NUMERO.format(primeiroDaPagina)}–${NUMERO.format(
          primeiroDaPagina + itens.length - 1,
        )} de ${NUMERO.format(total)} · página ${String(pagina)} de ${String(totalDePaginas)}`

  /*
   * O `!isError` na frente importa: numa recusa `consulta.data` também é `undefined`, e sem ele
   * o erro seria lido como "ainda carregando" — a navegação sumiria junto com a lista, e quem
   * tomou um `500` na página 3 ficaria sem nenhuma saída.
   */
  const carregando = !consulta.isError && (consulta.isLoading || consulta.data === undefined)

  /*
   * Enquanto a página seguinte não chega, `keepPreviousData` mantém as linhas da anterior na
   * tela — e `pagina` **já** avançou. Anunciar "51–100 · página 2" sobre as linhas da página 1
   * seria a tela afirmando algo que quem olha consegue desmentir.
   *
   * A saída é não afirmar nada nessa janela: enquanto o dado é placeholder, o lugar da posição
   * diz "Atualizando…". Guardar a última posição assentada daria mais informação, mas exigiria
   * um estado espelhando o que a consulta já sabe — e um número velho ao lado de linhas que
   * estão trocando é a mesma classe de erro, só que mais difícil de perceber. Quando o dado
   * assenta, a posição volta inteira, e ela sempre descreve as linhas que estão na tela.
   */
  const atualizando = consulta.isPlaceholderData
  const posicaoMostrada = atualizando ? ATUALIZANDO : posicaoAtual

  /*
   * A base encolheu debaixo de quem estava paginando (outra pessoa excluiu, a busca mudou o
   * conjunto, a RLS passou a esconder linhas) e `pagina` ficou além do fim. Sem isto a tela
   * diria "Nenhuma empresa cadastrada ainda." com a base cheia atrás. A correção é da **URL**,
   * como todo o resto do estado desta tela, e só acontece com dado assentado — corrigir sobre
   * um placeholder mandaria a pessoa para uma página calculada a partir de números velhos.
   *
   * A guarda é **"há resposta"**, e não "há linhas": com `total > 0` no lugar dela, uma busca
   * sem resultado numa página funda (`?busca=zzzz&pagina=3`, de um link ou da barra de
   * endereço) não se corrigia, e o rodapé dizia "Página 3 de 1" — a tela se contradizendo em
   * uma frase. Numa recusa `consulta.data` é `undefined`, e por isso ela também é o que impede
   * a correção de jogar para a primeira página quem tomou um `500` na terceira.
   */
  const alemDoFim =
    !carregando && !atualizando && consulta.data !== undefined && pagina > totalDePaginas
  useEffect(() => {
    if (alemDoFim) {
      aoTrocarAPagina(totalDePaginas, { substituir: true })
    }
  }, [alemDoFim, aoTrocarAPagina, totalDePaginas])

  function mensagemDeVazio(): string {
    if (alemDoFim) {
      return PAGINA_VAZIA
    }
    // `termoConsultado`, e não `busca`: quem produziu esta lista vazia foi o termo que **foi**
    // ao serviço. Apagar o campo zera `busca` na mesma tecla, e por um quadro a tela diria
    // "Nenhuma empresa cadastrada ainda." com a base cheia atrás, à espera da consulta nova.
    return termoConsultado.trim() === '' ? SEM_EMPRESAS : SEM_RESULTADO
  }

  return (
    <section className="empresas" aria-labelledby={ID_DO_TITULO}>
      <div className="empresas__cartao">
        <div className="empresas__cabecalho">
          {/* O nome da região continua para o leitor de tela; na tela não há caixa nem texto
              (protótipo v2): a busca, a contagem e a tabela são o corpo da tela. */}
          <h2 className="empresas__titulo empresas__so-para-leitor" id={ID_DO_TITULO}>
            {NOME_DO_CARTAO}
          </h2>
          <span className="empresas__so-para-leitor">{AJUDA}</span>
        </div>

        {/* A ordem da tela (13.15, chamado de 26/10, A), nas duas larguras: as ações, o
            gerenciador e o aviso, e só então a barra colada à tabela — a busca junto das colunas
            que filtra. O `+ Empresa` vai ao cabeçalho do shell (protótipo v2); o nome acessível
            continua o de sempre. */}
        <AcoesDoCabecalho>
          <Link
            to={CAMINHO_DA_EMPRESA_NOVA}
            className="empresas__botao empresas__botao--primario"
            aria-label="Nova empresa"
          >
            + Empresa
          </Link>
        </AcoesDoCabecalho>
        {gerenciando ? (
          <GerenciadorDeEtiquetas
            tipo="empresa"
            id={ID_DO_GERENCIADOR}
            aoFechar={() => {
              setGerenciando(false)
              botaoDeEtiquetas.current?.focus()
            }}
            etiquetaDoFiltro={etiqueta}
            aoApagarAEtiquetaDoFiltro={() => {
              aoTrocarAEtiqueta?.(undefined)
            }}
          />
        ) : null}
        {avisoDasEscondidas === undefined ? null : (
          <p className="empresas__ajuda empresas__escondidas" role="status">
            {avisoDasEscondidas}
          </p>
        )}

        <div className="empresas__ferramentas">
          <div className="empresas__campo">
            <label className="empresas__rotulo empresas__so-para-leitor" htmlFor={ID_DA_BUSCA}>
              {ROTULO_DA_BUSCA}
            </label>
            <input
              id={ID_DA_BUSCA}
              className="empresas__entrada"
              type="search"
              value={busca}
              // O mesmo teto do serviço: sem ele, colar 101 caracteres trocaria a lista por um
              // `422` cru do FastAPI — o serviço explicando um limite que a tela nunca mostrou.
              maxLength={TAMANHO_DA_BUSCA}
              placeholder="Buscar por nome ou CNPJ"
              onChange={(evento) => {
                aoTrocarABusca(evento.target.value)
              }}
            />
          </div>
          <div className="empresas__direita">
            <FiltroPorEtiqueta
              tipo="empresa"
              valor={etiqueta}
              aoTrocar={(etiquetaId) => {
                aoTrocarAEtiqueta?.(etiquetaId)
              }}
            />
            <BotaoDeEtiquetas
              aberto={gerenciando}
              controla={ID_DO_GERENCIADOR}
              refDoBotao={botaoDeEtiquetas}
              aoTrocar={() => {
                setGerenciando((aberto) => !aberto)
              }}
            />
            {consulta.data === undefined ? null : (
              <span className="empresas__contagem">
                {`${NUMERO.format(total)} empresa${total === 1 ? '' : 's'}`}
              </span>
            )}
            <MenuDeColunas visiveis={visiveis} aoTrocar={trocarAColuna} />
          </div>
        </div>

        {/* `aria-busy` é o sinal de "estou atualizando" sem apagar nada: as linhas antigas
            continuam legíveis, e quem usa leitor de tela sabe que elas vão trocar. */}
        <div
          className="empresas__corpo"
          aria-busy={atualizando}
          data-atualizando={atualizando ? 'sim' : 'nao'}
        >
          {filtro.removida ? (
            /* 13.18: a etiqueta da URL não está no catálogo desta tela — "Etiqueta removida" no
               filtro, com "Limpar filtro", e a lista nem foi chamada. */
            <p className="empresas__ajuda" role="status">
              {FILTRO_SEM_A_ETIQUETA}
            </p>
          ) : consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="empresas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `|| data === undefined` é o que fecha o terceiro estado: sem rede, a consulta
                  fica **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela diria
                  "nenhuma empresa cadastrada", afirmando ausência onde só há desconhecimento. */
          carregando ? (
            <p className="empresas__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : itens.length === 0 ? (
            <p className="empresas__ajuda">{mensagemDeVazio()}</p>
          ) : ehCelular ? (
            <Cards linhas={linhas} visiveis={visiveis} />
          ) : (
            <Tabela linhas={linhas} visiveis={visiveis} />
          )}
        </div>

        {/*
         * A navegação sobrevive ao erro, de propósito: quem estava na página 3 e tomou um `500`
         * precisa de alguma saída — voltar uma página, ou tentar de novo. Some-la junto com a
         * lista deixaria a pessoa numa tela sem nada a fazer além de recarregar no navegador.
         */}
        {carregando || filtro.removida ? null : (
          <nav className="empresas__paginacao" aria-label="Paginação">
            <button
              type="button"
              className="empresas__botao empresas__botao--discreto"
              disabled={pagina === 1}
              onClick={() => {
                aoTrocarAPagina(Math.max(1, pagina - 1))
              }}
            >
              Anterior
            </button>
            {consulta.isError ? (
              <button
                type="button"
                className="empresas__botao empresas__botao--discreto"
                onClick={() => {
                  void consulta.refetch()
                }}
              >
                {TENTAR_DE_NOVO}
              </button>
            ) : (
              /* A posição é declarada, e não deduzida do que está na tela: quem pagina precisa
                 saber onde está no alfabeto para conseguir voltar. */
              <span
                className="empresas__posicao"
                role="status"
                data-atualizando={atualizando ? 'sim' : 'nao'}
              >
                {posicaoMostrada}
              </span>
            )}
            <button
              type="button"
              className="empresas__botao empresas__botao--discreto"
              disabled={!consulta.isError && pagina >= totalDePaginas}
              onClick={() => {
                aoTrocarAPagina(pagina + 1)
              }}
            >
              Próxima
            </button>
          </nav>
        )}
      </div>
    </section>
  )
}
